import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import "../../styles/Article.css";
import { supabase } from "../../config/Supabase";
import SEO from "../utils/SEO";
import SocialShare from "../utils/SocialShare";
import SubscriptionComponent from "../utils/SubscriptionComponent";
import ArticleBody from "./ArticleBody";
import { trackArticleRead, trackCTAClick } from "../../utils/analytics";

const BASE_URL = "https://joinsarv.com";

// Public page for an admin-authored (Supabase) article at /learn/post/<slug>.
// Renders with the exact same UI as the static JSON articles (ArticleBody).
const DbArticle = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    let active = true;
    (async () => {
      const { data, error } = await supabase
        .from("articles")
        .select("*")
        .eq("slug", slug)
        .eq("status", "published")
        .maybeSingle();
      if (!active) return;
      if (error || !data) {
        setStatus("notfound");
        return;
      }
      setArticle(data);
      setStatus("ok");
      trackArticleRead(data.title, "db");
      // Related: other published posts (prefer same category).
      const { data: rel } = await supabase
        .from("articles")
        .select("slug,title,category")
        .eq("status", "published")
        .neq("slug", slug)
        .limit(12);
      if (!active) return;
      const pool = rel || [];
      const sameCat = pool.filter((a) => data.category && a.category === data.category);
      setRelated((sameCat.length ? sameCat : pool).slice(0, 4));
    })();
    return () => {
      active = false;
    };
  }, [slug]);

  if (status === "loading") {
    return <div className="article-page__loading">Loading…</div>;
  }
  if (status === "notfound" || !article) {
    return (
      <div style={{ paddingTop: "120px", paddingBottom: "100px", textAlign: "center" }}>
        <SEO title="Article not found - Sarv" description="This article could not be found." noIndex={true} />
        <h1>Article not found</h1>
        <p>
          Browse the <Link to="/learn">Sarv health library</Link>.
        </p>
      </div>
    );
  }

  const url = `${BASE_URL}/learn/post/${article.slug}`;
  const image = article.hero_image_url || `${BASE_URL}/img/og-image.jpg`;
  const metaTitle = article.meta_title || `${article.title} - Sarv Health Library`;
  const description =
    article.meta_description ||
    (article.summary && article.summary.slice(0, 155)) ||
    (Array.isArray(article.content)
      ? (article.content.find((b) => b && b.type === "paragraph")?.text || "").slice(0, 155)
      : "");
  const keywords = article.focus_keyphrase
    ? `${article.focus_keyphrase}, ${article.title}, ${article.category || "herbal medicine"}, natural remedies`
    : `${article.title}, ${article.category || "herbal medicine"}, natural remedies, holistic health`;
  const faqItems = Array.isArray(article.faq) ? article.faq.filter((f) => f && f.q && f.a) : [];
  const updatedDate = article.updated_at || article.published_at;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description,
    image,
    author: { "@type": "Organization", name: article.author || "Sarv Health Team", url: BASE_URL },
    publisher: {
      "@type": "Organization",
      name: "Sarv",
      url: BASE_URL,
      logo: { "@type": "ImageObject", url: `${BASE_URL}/v.png` },
    },
    ...(article.published_at ? { datePublished: article.published_at } : {}),
    ...(article.updated_at ? { dateModified: article.updated_at } : {}),
    ...(article.focus_keyphrase ? { keywords: article.focus_keyphrase } : {}),
    ...(Array.isArray(article.sources) && article.sources.length
      ? { citation: article.sources }
      : {}),
    url,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: BASE_URL },
      { "@type": "ListItem", position: 2, name: "Library", item: `${BASE_URL}/learn` },
      { "@type": "ListItem", position: 3, name: article.title, item: url },
    ],
  };
  const faqSchema = faqItems.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqItems.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  return (
    <>
      <SEO
        title={metaTitle}
        description={description}
        canonicalPath={`/learn/post/${article.slug}`}
        image={image}
        type="article"
        articleData={{ publishedTime: article.published_at, modifiedTime: article.updated_at }}
        keywords={keywords}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      {faqSchema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      )}

      <article className="article-page">
        <nav className="article-page__breadcrumb" aria-label="breadcrumb">
          <Link to="/">Home</Link> › <Link to="/learn">Library</Link> › <span>{article.title}</span>
        </nav>

        <div className="article-page__meta">
          <span className="article-page__byline">By {article.author || "Sarv Health Team"}</span>
          {updatedDate && (
            <span className="article-page__updated">
              {" · Updated "}
              {new Date(updatedDate).toLocaleDateString("en-US", { dateStyle: "long" })}
            </span>
          )}
        </div>
        <p className="article-page__review">
          Medically reviewed by the Sarv Health Team ·{" "}
          <Link to="/editorial-process">Our editorial process</Link>
        </p>

        <h1 className="article-page__title">{article.title}</h1>

        {article.hero_image_url && (
          <figure className="article-page__hero-wrap">
            <img
              src={article.hero_image_url}
              alt={article.title}
              className="article-page__hero"
              loading="eager"
            />
          </figure>
        )}

        <div className="article-page__body">
          <ArticleBody content={article.content} sources={article.sources} />
        </div>

        {faqItems.length > 0 && (
          <section className="article-page__faq">
            <h2>Frequently asked questions</h2>
            {faqItems.map((f, i) => (
              <div key={i} className="article-page__faq-item">
                <h3>{f.q}</h3>
                <p>{f.a}</p>
              </div>
            ))}
          </section>
        )}

        <p className="article-page__disclaimer">
          This article is for general wellness and educational purposes only and is not
          medical advice. Consult a qualified healthcare provider before using herbs —
          especially if you are pregnant, nursing, or taking medication.
        </p>

        <aside className="article-page__cta">
          <p>
            Curious which natural remedies fit your body type?{" "}
            <Link
              to="/quiz"
              onClick={() => trackCTAClick("article_end_dosha_quiz", "article-end")}
            >
              Take the free Ayurvedic dosha quiz →
            </Link>
          </p>
        </aside>

        <p className="article-page__editorial">
          <Link to="/editorial-process" className="article-page__editorial-link">
            Our editorial process
          </Link>
        </p>

        <SocialShare />

        {related.length > 0 && (
          <section className="article-page__related">
            <h2>Related articles</h2>
            <ul>
              {related.map((a) => (
                <li key={a.slug}>
                  <Link to={`/learn/post/${a.slug}`}>{a.title}</Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>

      <SubscriptionComponent />
    </>
  );
};

export default DbArticle;
